import { useCallback, useEffect, useState } from 'react';
import { locations as locationsApi, stores as storesApi } from '../api/endpoints';
import { useToast } from '../context/ToastContext';
import { Loading, Empty, Modal, Can } from '../components/Common';
import LocationPicker from '../components/LocationPicker';

const TABS = [
  { key: 'countries', label: 'Countries' },
  { key: 'cities', label: 'Cities' },
  { key: 'areas', label: 'Service Areas' },
];

/**
 * Countries, cities and service areas.
 *
 * Countries drive the dial-code picker on the app's sign-in sheet. Areas decide
 * serviceability: during onboarding the app sends the customer's coordinates and
 * the API matches them against these areas, so switching one off here is what
 * makes the app say "we don't deliver there yet".
 */
export default function Locations() {
  const [tab, setTab] = useState('countries');

  useEffect(() => { document.title = 'Locations · UrbanClean'; }, []);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Locations</h1>
          <div className="sub">
            Countries for the phone-code picker, and the areas you actually serve
          </div>
        </div>
      </div>

      <div className="toolbar">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`btn btn-sm ${tab === t.key ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'countries' ? <Countries /> : null}
      {tab === 'cities' ? <Cities /> : null}
      {tab === 'areas' ? <Areas /> : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Countries                                                                   */
/* -------------------------------------------------------------------------- */

function Countries() {
  const toast = useToast();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await locationsApi.countries();
      setList(res.data.countries);
    } catch (err) {
      toast.error('Could not load countries', err.message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const remove = async (country) => {
    if (!window.confirm(`Remove ${country.name}?`)) return;
    try {
      await locationsApi.removeCountry(country._id);
      toast.success('Country removed');
      load();
    } catch (err) {
      toast.error('Could not remove', err.message);
    }
  };

  if (loading) return <Loading />;

  return (
    <>
      <div className="card mb">
        <div className="card-head">
          <h3>Countries</h3>
          <Can permission="location.create">
            <button className="btn btn-sm btn-blue" onClick={() => setEditing({})}>+ Add country</button>
          </Can>
        </div>
        {list.length === 0 ? (
          <Empty title="No countries yet" body="Add one to make its dial code selectable at sign-in." />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Country</th><th>Dial code</th><th>Phone length</th>
                  <th>Currency</th><th className="center">Cities</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c._id}>
                    <td>
                      <span style={{ fontSize: 17, marginRight: 8 }}>{c.flag}</span>
                      <span className="strong">{c.name}</span>
                      <span className="muted mono" style={{ marginLeft: 6, fontSize: 12 }}>{c.code}</span>
                    </td>
                    <td className="mono strong">{c.dialCode}</td>
                    <td className="muted">
                      {c.phoneMinLength === c.phoneMaxLength
                        ? `${c.phoneMinLength} digits`
                        : `${c.phoneMinLength}–${c.phoneMaxLength} digits`}
                    </td>
                    <td className="muted">{c.currencySymbol} {c.currency}</td>
                    <td className="center">{c.cityCount}</td>
                    <td>
                      <span className={`badge badge-${c.isActive ? 'green' : 'gray'}`}>
                        {c.isActive ? 'Selectable' : 'Hidden'}
                      </span>
                    </td>
                    <td className="right nowrap">
                      <Can permission="location.update">
                        <button className="btn btn-sm btn-ghost" onClick={() => setEditing(c)}>Edit</button>
                      </Can>
                      <Can permission="location.delete">
                        <button className="btn btn-sm btn-ghost" style={{ marginLeft: 6, color: 'var(--red)' }} onClick={() => remove(c)}>
                          Delete
                        </button>
                      </Can>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CountryEditor country={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </>
  );
}

function CountryEditor({ country, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!country) return;
    setForm({
      name: country.name || '', code: country.code || '', dialCode: country.dialCode || '',
      flag: country.flag || '', phoneMinLength: country.phoneMinLength ?? 10,
      phoneMaxLength: country.phoneMaxLength ?? 10,
      currency: country.currency || 'INR', currencySymbol: country.currencySymbol || '₹',
      sortOrder: country.sortOrder ?? 100, isActive: country.isActive !== false,
    });
  }, [country]);

  if (!country) return null;
  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const save = async () => {
    if (!form.name || !form.code || !form.dialCode) {
      return toast.error('Name, ISO code and dial code are required');
    }
    setBusy(true);
    try {
      const body = {
        ...form,
        phoneMinLength: Number(form.phoneMinLength),
        phoneMaxLength: Number(form.phoneMaxLength),
        sortOrder: Number(form.sortOrder),
      };
      if (country._id) await locationsApi.updateCountry(country._id, body);
      else await locationsApi.createCountry(body);
      toast.success('Country saved');
      onSaved();
    } catch (err) {
      toast.error('Could not save', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open title={country._id ? `Edit — ${country.name}` : 'Add country'} onClose={onClose}
      footer={<>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
      </>}>
      <div className="row">
        <div className="field"><label>Country name *</label><input className="input" value={form.name || ''} onChange={set('name')} placeholder="India" /></div>
        <div className="field">
          <label>ISO code *</label>
          <input className="input mono" value={form.code || ''} maxLength={2}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="IN" />
        </div>
      </div>
      <div className="row">
        <div className="field">
          <label>Dial code *</label>
          <input className="input mono" value={form.dialCode || ''} onChange={set('dialCode')} placeholder="+91" />
          <div className="hint">Shown in the app's phone-code picker.</div>
        </div>
        <div className="field">
          <label>Flag emoji</label>
          <input className="input" value={form.flag || ''} onChange={set('flag')} placeholder="🇮🇳" style={{ fontSize: 17 }} />
        </div>
      </div>
      <div className="row">
        <div className="field">
          <label>Min phone length</label>
          <input className="input" type="number" value={form.phoneMinLength} onChange={set('phoneMinLength')} />
        </div>
        <div className="field">
          <label>Max phone length</label>
          <input className="input" type="number" value={form.phoneMaxLength} onChange={set('phoneMaxLength')} />
          <div className="hint">Digits after the dial code. Sign-in validates against this.</div>
        </div>
      </div>
      <div className="row">
        <div className="field"><label>Currency</label><input className="input" value={form.currency || ''} onChange={set('currency')} /></div>
        <div className="field"><label>Symbol</label><input className="input" value={form.currencySymbol || ''} onChange={set('currencySymbol')} /></div>
        <div className="field"><label>Sort order</label><input className="input" type="number" value={form.sortOrder} onChange={set('sortOrder')} /></div>
      </div>
      <div className="field">
        <label className="switch">
          <input type="checkbox" checked={form.isActive} onChange={set('isActive')} />
          <span className="track" /><span>Selectable in the app</span>
        </label>
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* Cities                                                                      */
/* -------------------------------------------------------------------------- */

function Cities() {
  const toast = useToast();
  const [list, setList] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [cities, countries] = await Promise.all([locationsApi.cities(), locationsApi.countries()]);
      setList(cities.data.cities);
      setCountryList(countries.data.countries);
    } catch (err) {
      toast.error('Could not load cities', err.message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const remove = async (city) => {
    if (!window.confirm(`Remove ${city.name}?`)) return;
    try {
      await locationsApi.removeCity(city._id);
      toast.success('City removed');
      load();
    } catch (err) {
      toast.error('Could not remove', err.message);
    }
  };

  if (loading) return <Loading />;

  return (
    <>
      <div className="card mb">
        <div className="card-head">
          <h3>Cities</h3>
          <Can permission="location.create">
            <button className="btn btn-sm btn-blue" onClick={() => setEditing({})}>+ Add city</button>
          </Can>
        </div>
        {list.length === 0 ? (
          <Empty title="No cities yet" body="Add a city, then the areas inside it." />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr><th>City</th><th>Country</th><th className="center">Areas</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c._id}>
                    <td>
                      <div className="strong">{c.name}</div>
                      <div className="muted" style={{ fontSize: 12 }}>{c.state}</div>
                    </td>
                    <td className="muted">{c.country?.name}</td>
                    <td className="center">{c.areaCount}</td>
                    <td>
                      <span className={`badge badge-${c.isServiceable && c.isActive ? 'green' : 'amber'}`}>
                        {!c.isActive ? 'Hidden' : c.isServiceable ? 'Live' : 'Coming soon'}
                      </span>
                    </td>
                    <td className="right nowrap">
                      <Can permission="location.update">
                        <button className="btn btn-sm btn-ghost" onClick={() => setEditing(c)}>Edit</button>
                      </Can>
                      <Can permission="location.delete">
                        <button className="btn btn-sm btn-ghost" style={{ marginLeft: 6, color: 'var(--red)' }} onClick={() => remove(c)}>
                          Delete
                        </button>
                      </Can>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CityEditor city={editing} countryList={countryList} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </>
  );
}

function CityEditor({ city, countryList, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!city) return;
    setForm({
      country: city.country?._id || city.country || (countryList[0]?._id ?? ''),
      name: city.name || '', state: city.state || '',
      lat: city.lat ?? '', lng: city.lng ?? '',
      isActive: city.isActive !== false,
      isServiceable: city.isServiceable !== false,
    });
  }, [city, countryList]);

  if (!city) return null;
  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const save = async () => {
    if (!form.country || !form.name) return toast.error('Country and city name are required');
    setBusy(true);
    try {
      const body = {
        ...form,
        lat: form.lat === '' ? null : Number(form.lat),
        lng: form.lng === '' ? null : Number(form.lng),
      };
      if (city._id) await locationsApi.updateCity(city._id, body);
      else await locationsApi.createCity(body);
      toast.success('City saved');
      onSaved();
    } catch (err) {
      toast.error('Could not save', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open title={city._id ? `Edit — ${city.name}` : 'Add city'} onClose={onClose}
      footer={<>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
      </>}>
      <div className="field">
        <label>Country *</label>
        <select className="select" value={form.country || ''} onChange={set('country')}>
          {countryList.map((c) => <option key={c._id} value={c._id}>{c.flag} {c.name}</option>)}
        </select>
      </div>
      <div className="row">
        <div className="field"><label>City name *</label><input className="input" value={form.name || ''} onChange={set('name')} /></div>
        <div className="field"><label>State / region</label><input className="input" value={form.state || ''} onChange={set('state')} /></div>
      </div>
      <LocationPicker
        lat={form.lat}
        lng={form.lng}
        onChange={({ lat, lng }) => setForm((f) => ({ ...f, lat, lng }))}
      />
      <div className="field">
        <label className="switch">
          <input type="checkbox" checked={form.isServiceable} onChange={set('isServiceable')} />
          <span className="track" /><span>Taking orders here</span>
        </label>
        <div className="hint">Off shows "coming soon" in the app and blocks every area inside this city.</div>
      </div>
      <div className="field">
        <label className="switch">
          <input type="checkbox" checked={form.isActive} onChange={set('isActive')} />
          <span className="track" /><span>Visible</span>
        </label>
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* Areas                                                                       */
/* -------------------------------------------------------------------------- */

function Areas() {
  const toast = useToast();
  const [list, setList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [cityFilter, setCityFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [areas, cities, stores] = await Promise.all([
        locationsApi.areas(cityFilter ? { city: cityFilter } : {}),
        locationsApi.cities(),
        storesApi.list({ limit: 100 }).catch(() => ({ data: { stores: [] } })),
      ]);
      setList(areas.data.areas);
      setCityList(cities.data.cities);
      setStoreList(stores.data.stores);
    } catch (err) {
      toast.error('Could not load areas', err.message);
    } finally {
      setLoading(false);
    }
  }, [cityFilter, toast]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (area) => {
    try {
      const res = await locationsApi.toggleArea(area._id);
      toast.success(res.message);
      load();
    } catch (err) {
      toast.error('Could not update', err.message);
    }
  };

  const remove = async (area) => {
    if (!window.confirm(`Remove ${area.name}?`)) return;
    try {
      await locationsApi.removeArea(area._id);
      toast.success('Area removed');
      load();
    } catch (err) {
      toast.error('Could not remove', err.message);
    }
  };

  return (
    <>
      <div className="toolbar">
        <select className="select" value={cityFilter} onChange={(e) => { setCityFilter(e.target.value); setLoading(true); }}>
          <option value="">All cities</option>
          {cityList.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
        </select>
        <div className="spacer" />
        <Can permission="location.create">
          <button className="btn btn-blue btn-sm" onClick={() => setEditing({})}>+ Add area</button>
        </Can>
      </div>

      <div className="card mb">
        {loading ? <Loading /> : list.length === 0 ? (
          <Empty
            title="No service areas yet"
            body="Add the areas you cover. The app matches a customer's location against these during sign-up."
          />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Area</th><th>City</th><th>Pincodes</th>
                  <th>Coverage</th><th>Store</th><th>Serviceable</th><th></th>
                </tr>
              </thead>
              <tbody>
                {list.map((a) => (
                  <tr key={a._id}>
                    <td>
                      <div className="strong">{a.name}</div>
                      {a.unavailableMessage ? (
                        <div className="muted" style={{ fontSize: 11.5, maxWidth: 240 }}>{a.unavailableMessage}</div>
                      ) : null}
                    </td>
                    <td className="muted">{a.city?.name}</td>
                    <td className="muted mono" style={{ fontSize: 12 }}>
                      {a.pincodes?.length ? a.pincodes.join(', ') : '—'}
                    </td>
                    <td className="muted">
                      {a.location?.coordinates?.length
                        ? <>{a.radiusKm} km radius<div style={{ fontSize: 11 }}>
                            {a.location.coordinates[1].toFixed(4)}, {a.location.coordinates[0].toFixed(4)}
                          </div></>
                        : 'Pincode only'}
                    </td>
                    <td className="muted">{a.store?.name || '—'}</td>
                    <td>
                      <span className={`badge badge-${a.isServiceable ? 'green' : 'red'}`}>
                        {a.isServiceable ? 'Serving' : 'Not serving'}
                      </span>
                    </td>
                    <td className="right nowrap">
                      <Can permission="location.update">
                        <button className="btn btn-sm btn-ghost" onClick={() => toggle(a)}>
                          {a.isServiceable ? 'Switch off' : 'Switch on'}
                        </button>
                        <button className="btn btn-sm btn-ghost" style={{ marginLeft: 6 }} onClick={() => setEditing(a)}>Edit</button>
                      </Can>
                      <Can permission="location.delete">
                        <button className="btn btn-sm btn-ghost" style={{ marginLeft: 6, color: 'var(--red)' }} onClick={() => remove(a)}>
                          Delete
                        </button>
                      </Can>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AreaEditor
        area={editing}
        cityList={cityList}
        storeList={storeList}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </>
  );
}

function AreaEditor({ area, cityList, storeList, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!area) return;
    const coords = area.location?.coordinates || [];
    setForm({
      city: area.city?._id || area.city || (cityList[0]?._id ?? ''),
      name: area.name || '',
      pincodes: (area.pincodes || []).join(', '),
      lat: coords.length ? coords[1] : '',
      lng: coords.length ? coords[0] : '',
      radiusKm: area.radiusKm ?? 4,
      store: area.store?._id || area.store || '',
      isServiceable: area.isServiceable !== false,
      unavailableMessage: area.unavailableMessage || '',
    });
  }, [area, cityList]);

  if (!area) return null;
  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const save = async () => {
    if (!form.city || !form.name) return toast.error('City and area name are required');
    setBusy(true);
    try {
      const body = {
        city: form.city,
        name: form.name,
        pincodes: form.pincodes.split(',').map((p) => p.trim()).filter(Boolean),
        radiusKm: Number(form.radiusKm) || 4,
        store: form.store || null,
        isServiceable: form.isServiceable,
        unavailableMessage: form.unavailableMessage,
      };
      // Only send coordinates when both are present, so a pincode-only area works.
      if (form.lat !== '' && form.lng !== '') {
        body.lat = Number(form.lat);
        body.lng = Number(form.lng);
      }

      if (area._id) await locationsApi.updateArea(area._id, body);
      else await locationsApi.createArea(body);
      toast.success('Area saved');
      onSaved();
    } catch (err) {
      toast.error('Could not save', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open title={area._id ? `Edit — ${area.name}` : 'Add service area'} onClose={onClose}
      footer={<>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save area'}</button>
      </>}>
      <div className="row">
        <div className="field">
          <label>City *</label>
          <select className="select" value={form.city || ''} onChange={set('city')}>
            {cityList.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        </div>
        <div className="field"><label>Area name *</label><input className="input" value={form.name || ''} onChange={set('name')} placeholder="Pitampura" /></div>
      </div>

      <div className="field">
        <label>Pincodes</label>
        <input className="input" value={form.pincodes || ''} onChange={set('pincodes')} placeholder="110034, 110088" />
        <div className="hint">Comma separated. Used when a customer types a pincode instead of sharing GPS.</div>
      </div>

      <LocationPicker
        lat={form.lat}
        lng={form.lng}
        onChange={({ lat, lng }) => setForm((f) => ({ ...f, lat, lng }))}
      />

      <div className="field">
        <label>Coverage radius (km)</label>
        <input className="input" type="number" value={form.radiusKm} onChange={set('radiusKm')} style={{ maxWidth: 140 }} />
        <div className="hint">
          A customer's GPS position counts as covered when it falls inside this radius of the centre point.
        </div>
      </div>

      <div className="field">
        <label>Served by store</label>
        <select className="select" value={form.store || ''} onChange={set('store')}>
          <option value="">Route by pincode</option>
          {storeList.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
        </select>
      </div>

      <div className="field">
        <label className="switch">
          <input type="checkbox" checked={form.isServiceable} onChange={set('isServiceable')} />
          <span className="track" /><span>Currently serving this area</span>
        </label>
      </div>

      {!form.isServiceable ? (
        <div className="field">
          <label>Message shown in the app</label>
          <input
            className="input"
            value={form.unavailableMessage || ''}
            onChange={set('unavailableMessage')}
            placeholder="We are not covering this area yet."
          />
        </div>
      ) : null}
    </Modal>
  );
}
